/**
 * The inline direction an element renders in, read from the nearest `dir`
 * attribute (#1098).
 *
 * The application has no locale switch of its own yet: a Hebrew deployment
 * sets `dir="rtl"` on the document, exactly as `tests/e2e/canonical-surfaces.spec.ts`
 * does, and every surface has to mirror from that one attribute. Keyboard
 * conventions mirror with it — in a right-to-left tab list the "next" tab is
 * to the LEFT, so an arrow key has to be read against the direction the strip
 * actually renders in rather than a fixed physical side.
 *
 * Read from the DOM rather than `getComputedStyle(...).direction` because
 * jsdom does not derive the computed `direction` from a `dir` attribute, and a
 * test that cannot reach the branch is a branch nobody proves. `dir="auto"` is
 * content-dependent and resolves as the enclosing direction here; nothing this
 * helper serves puts a control inside an `auto` container.
 */
export type TextDirection = "ltr" | "rtl";

export function closestDirection(element: Element | null): TextDirection {
  let current: Element | null = element;
  while (current !== null) {
    const declared = current.getAttribute("dir")?.toLowerCase();
    if (declared === "rtl") {
      return "rtl";
    }
    if (declared === "ltr") {
      return "ltr";
    }
    current = current.parentElement;
  }
  return "ltr";
}

/**
 * Which way an arrow key moves through a horizontal strip of controls, given
 * the strip's direction: `1` for the next control, `-1` for the previous,
 * `0` when the key is not a horizontal arrow.
 */
export function horizontalStep(
  key: string,
  direction: TextDirection,
): -1 | 0 | 1 {
  if (key === "ArrowRight") {
    return direction === "rtl" ? -1 : 1;
  }
  if (key === "ArrowLeft") {
    return direction === "rtl" ? 1 : -1;
  }
  return 0;
}
